/**
 * Arabic catalog (RTL) for the Phase 2 `MessageId` set — Deliverable A.
 *
 * `dir: 'rtl'`. Stable technical grade tokens (W/M) are LTR-isolated INSIDE the pattern
 * with LRI (U+2066) … PDI (U+2069) so they keep left-to-right order and Latin digits
 * (`::numbering-system/latn`) within the surrounding RTL text — copy-paste-stable and never
 * visually reordered. Glyph shaping (Arabic joining forms) is delegated to the platform
 * shaper; the catalog only supplies text.
 *
 * Translations are Modern Standard Arabic. Per INV-STABLE-ID, accuracy here is a
 * presentation concern only — a wrong translation would still leave every verdict fixed.
 */
import { P2 } from '../message-ids-phase2.js';
// Unicode bidi isolates (kept inline so the technical token is isolated in the pattern).
const LRI = '⁦';
const PDI = '⁩';
const grade = (token, arg) => `${LRI}${token}{${arg}, number, ::numbering-system/latn}${PDI}`;
export const AR_CATALOG = {
    locale: 'ar',
    dir: 'rtl',
    messages: {
        [P2.inquiryTitle]: 'الاستقصاء',
        [P2.sectionClaims]: 'الادعاءات',
        [P2.sectionIntegrity]: 'السلامة',
        [P2.axisWarrantLabel]: 'التسويغ',
        [P2.axisWarrantValue]: grade('W', 'w'),
        [P2.axisEmpiricalLabel]: 'التجريبي',
        [P2.axisEmpiricalValue]: '{kind, select, defn {تعريف} interp {تفسير} testable {قابل للاختبار} tested {مُختبَر} refuted {مدحوض} program {برنامج بحثي} philos {فلسفي} other {غير معروف}}',
        [P2.axisModalLabel]: 'الجِهة',
        [P2.axisModalValue]: grade('M', 'm'),
        [P2.axisKidSummary]: `مستوى القوة ${grade('W', 'w')}`,
        [P2.stateExplorable]: 'قابل للاستكشاف',
        [P2.stateRefuted]: 'مدحوض',
        [P2.stateOutside]: 'خارج النطاق',
        [P2.verdictConflict]: 'يتعارض مع {constraints}',
        [P2.verdictOutside]: 'يحتاج إلى {missing}',
        [P2.lossRegisterPlain]: 'مُبسَّط إلى لغة عادية — حُذف بعض التدقيق.',
        [P2.lossRegisterKid]: 'مُبسَّط للقُرّاء الصغار — حُذفت تفاصيل كثيرة.',
        [P2.lossProfileDetail]: 'يُلخَّص هنا ملفُّ التسويغ والتجريبي والجِهة الكامل.',
        [P2.lossConflictDetail]: 'تفاصيل التعارض مختصرة في هذا العرض.',
        [P2.laneVerified]: 'مُتحقَّق منه',
        [P2.laneUnverified]: 'غير مُتحقَّق منه',
        [P2.laneConflict]: 'ثبت التعارض',
        [P2.constraintLogic]: 'المنطق',
        [P2.constraintLightBends]: 'انحناء الضوء في حقل الجاذبية',
        [P2.constraintParallelPostulate]: 'مُسلَّمة التوازي',
        [P2.claimScalarGravity]: 'نظرية الجاذبية القياسية',
        [P2.claimNonEuclidean]: 'الهندسة اللاإقليدية',
        [P2.claimSubstrateSr]: 'تفسير الركيزة للنسبية الخاصة',
        [P2.edgeExtObservation]: 'رصد خارجي',
        [P2.edgeResourceSample]: 'مورد حوسبي مشترك',
        [P2.edgeHeuristicSample]: 'إرشاد استدلالي',
        [P2.edgeSharedSample]: 'أساس مشترك',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'القيود',
        [P2.sectionEdges]: 'الروابط',
        [P2.sectionFrame]: 'الإطار',
        [P2.sectionExposure]: 'الانكشاف',
        [P2.statusAdoptedFormal]: 'مُعتمَد (صُوري)',
        [P2.statusAdoptedInterpretive]: 'مُعتمَد (تفسيري)',
        [P2.statusSuspended]: 'مُعلَّق',
        [P2.edgeEffectConstraint]: 'يُركِّب قيدًا (قد يدحض)',
        [P2.edgeEffectResource]: 'مورد — لا أثر على الأحكام',
        [P2.edgeEffectHeuristic]: 'استدلال — لا أثر على الأحكام',
        [P2.edgeEffectShared]: 'أساس مشترك — لا أثر على الأحكام',
        [P2.frameActive]: '{count, plural, zero {لا قيود نشطة} one {قيد نشط واحد} two {قيدان نشطان} few {# قيود نشطة} many {# قيدًا نشطًا} other {# قيد نشط}}',
        [P2.exposureNone]: 'لا تبعيات تفسيرية',
        // `{id}` is a free-standing technical id (a Latin claim/constraint id) embedded in RTL
        // text — LTR-isolate it (LRI…PDI) so it keeps left-to-right order and stays copy-paste
        // stable, exactly as the W/M grade tokens are isolated above (Deliverable A bullet 3).
        [P2.exposureDep]: `يعتمد على ${LRI}{id}${PDI}`,
        [P2.laneTrackMachine]: 'الآلة',
        [P2.laneTrackExpert]: 'الخبير',
        [P2.laneTrackCommunity]: 'المجتمع',
        [P2.expertAudited]: 'مُدقَّق',
        [P2.expertInProgress]: 'قيد التنفيذ',
        [P2.expertNotReviewed]: 'لم يُراجَع',
        [P2.communityFlags]: 'توجد إشارات',
        [P2.communityNone]: 'لا إشارات',
        [P2.controlChallenge]: 'اعتراض',
        [P2.controlActivate]: 'تفعيل',
        [P2.controlMakeInterpretive]: 'جعله تفسيريًا',
        [P2.controlImportEdge]: 'استيراد رابط',
        [P2.controlPromote]: 'ترقية',
        [P2.controlUndo]: 'تراجع',
        [P2.controlRedo]: 'إعادة',
        [P2.controlRegister]: 'تسجيل',
        [P2.promoteRejected]: 'رُفضت الترقية',
        [P2.promoteAccepted]: 'قُبلت الترقية',
        [P2.promoteReasonRefutationTerminal]: 'البرهان لا يُعالج الدحض.',
        [P2.promoteReasonKeystone]: 'ترقية حجر الأساس تتطلب سندًا تجريبيًا.',
        [P2.promoteReasonEmpiricalNotFixed]: 'النوع التجريبي غير مُثبَّت.',
        [P2.promoteReasonModalNotFixed]: 'درجة الجِهة غير مُثبَّتة.',
        [P2.promoteReasonWarrantNotImproved]: 'درجة التسويغ لم تتحسّن.',
        [P2.evidenceComputation]: "حسب {engine} القيمة ⁦{op}⁩ = ⁦{result}⁩ ضمن {config}",
        [P2.lossNumericRounding]: "قُرِّبت النتيجة إلى {dp} منزلة عشرية",
    },
};
//# sourceMappingURL=ar.js.map